import type { QuotePayload } from "../api/QuoteSplitToCart";
import type { ProcessesedRows } from "../types/models";
import { isPackageRestrictionEnabled } from "~/features/PackageRestriction/utils/isPackageRestrictionEnabled";
import type { DetailedQuoteLineItemData } from "~/domains/orders/types/modelsV2";
import { getSessionSelectedAccount } from "~/domains/user/sessionRuntime";

interface QuotePreviewParams {
  items: ProcessesedRows[];
  quoteNumber: string;
}

type QuoteSplitSelectedLine = Pick<
  DetailedQuoteLineItemData,
  "lineNumber" | "packCode" | "packSize" | "lineNo" | "confirmedQty"
> & {
  netPrice: Pick<DetailedQuoteLineItemData["netPrice"], "currencyIso">;
};

interface QuoteSplitSourceLine {
  priceReference: DetailedQuoteLineItemData["priceReference"];
  product: Pick<DetailedQuoteLineItemData["product"], "code" | "fifteenDigit">;
  mainQuotes: QuoteSplitSelectedLine[];
  alternativeQuotes?: Array<{
    lineNumber: number;
    linesItem: QuoteSplitSelectedLine[];
  }>;
}

interface QuoteSplitSourceSummary {
  quoteNumber: string;
  quotesDetails: {
    products: QuoteSplitSourceLine[];
  };
}

export const getAccountInfo = (canSeeEndCustomer: boolean): string => {
  const userInfo = getSessionSelectedAccount();

  if (!userInfo) {
    return "";
  }
  const selectAccountDetails = `${userInfo.name ?? ""} (${userInfo.displayUid ?? ""})`;
  const endCustomerDetails = `${userInfo.endCustomerName ?? ""} (${userInfo.endCustomerCode ?? ""})`;

  return canSeeEndCustomer ? `${selectAccountDetails} // ${endCustomerDetails}` : selectAccountDetails;
};

export const buildQuotePayload = (
  quotePreviewData: QuotePreviewParams[],
  actualQuoteDetails: QuoteSplitSourceSummary[],
): QuotePayload[] => {
  return quotePreviewData.map((entry) => {
    const quoteData = actualQuoteDetails.find((item) => item.quoteNumber === entry.quoteNumber);
    const products = quoteData?.quotesDetails.products ?? [];
    return {
      quoteNumber: entry.quoteNumber,
      items: entry.items.map((row) => {
        return makeItemsPayload(row, products);
      }),
    };
  });
};

export function findMainQuote(quoteLines: QuoteSplitSourceLine[], position: number) {
  for (const item of quoteLines) {
    if (item.mainQuotes.length > 0) {
      const matchedMainQuote = item.mainQuotes.find((quote) => quote.lineNumber === position);
      if (matchedMainQuote) {
        return { product: item.product, mainQuote: matchedMainQuote, priceReference: item.priceReference };
      }
    }
    if (item.alternativeQuotes && item.alternativeQuotes.length > 0) {
      const alternateQuote = item.alternativeQuotes?.find((quote) => {
        return quote.lineNumber == position;
      });
      if (alternateQuote) {
        const matchedAlternateQuote = alternateQuote?.linesItem.find((line) => (line.lineNumber = position));
        return { product: item.product, mainQuote: matchedAlternateQuote, priceReference: item.priceReference };
      }
    }
  }

  throw new Error(`Main quote not found for position ${position}`);
}

export function makeItemsPayload(row: ProcessesedRows, quoteLines: QuoteSplitSourceLine[]) {
  const matchedQuote = findMainQuote(quoteLines, row.position);

  const packageCode = matchedQuote?.mainQuote?.packCode ?? "";
  const originCountry = packageCode ? matchedQuote.product.fifteenDigit[packageCode].countryOfOrigin : "";

  return {
    productCode: matchedQuote.product.code,
    packageCode,
    packageSize: matchedQuote?.mainQuote?.packSize,
    quoteLine: matchedQuote?.mainQuote?.lineNo,
    quantity: row.quantity,
    confirmedQuantity: matchedQuote?.mainQuote?.confirmedQty,
    selectedDeliveryDate: row.date,
    selectedDeliveryDateIsManual: row.hasDateBeenModified,
    priceReference: matchedQuote.priceReference,
    isRestrictedPackage: isPackageRestrictionEnabled(),
    originCountry,
    currencyIso: matchedQuote?.mainQuote?.netPrice.currencyIso ?? "",
  };
}
